import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { HubRpcClient } from '@monorepo/rpc-client';
import { clearForcedPasswordChange } from '@/lib/mfaStorage';
import config from '../config/index.js';

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null);
  const token = ref(null);
  const roles = ref([]);
  const permissions = ref([]);
  const isLoading = ref(false);
  const error = ref(null);

  // RPC Client with config
  const rpcClient = new HubRpcClient({
    baseURL: config.apiUrl + '/rpc',
    app: config._meta.app, // Pass app name from generated config
  });

  // Reset in-memory state on a 401. Without this, a stale Pinia store
  // can leak the previous session's roles/permissions into the next
  // login because applyAuthSession() persists whatever's in the refs
  // when the server response doesn't include a permissions block
  // (e.g. the `mfa@verify` response). handleUnauthorized in the RPC
  // client clears localStorage but cannot reach Pinia, so we listen
  // for its `session-expired` event here.
  if (typeof window !== 'undefined') {
    window.addEventListener('session-expired', () => {
      user.value = null;
      token.value = null;
      roles.value = [];
      permissions.value = [];
    });
  }

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const userName = computed(() => user.value?.name || '');
  const userEmail = computed(() => user.value?.email || '');

  /**
   * Does the server currently refuse everything but a password change?
   * (PCI DSS 8.3.5, #1198.) Read by the router guard, refreshed by
   * fetchPermissions() — the backend remains the authority.
   */
  const mustChangePassword = computed(() => user.value?.must_change_password === true);

  /**
   * Check whether the current user holds a specific action:resource permission.
   * Backend returns permissions as a flat string array (e.g. "list:exports").
   * Wildcard support: "*", "{action}:*", and "*:{resource}" all grant access.
   *
   * @param {string} action
   * @param {string} [resource='*']
   * @returns {boolean}
   */
  function hasPermission(action, resource = '*') {
    // Defensive: backend occasionally returns the actions list as a JSON object
    // with sequential numeric keys (PHP `json_encode` of a non-zero-indexed
    // array). Coerce to a plain array so .includes() works in either shape.
    const raw = permissions.value;
    const perms = Array.isArray(raw) ? raw : Object.values(raw ?? {});
    if (perms.includes('*')) return true;
    if (perms.includes(`${action}:${resource}`)) return true;
    if (perms.includes(`${action}:*`)) return true;
    if (perms.includes(`*:${resource}`)) return true;
    return false;
  }

  // Actions
  /**
   * Submit credentials. Returns a discriminated result so the caller can
   * branch on the MFA-aware response:
   *   - { status: 'authenticated' } → session persisted
   *   - { status: 'mfa_challenge', challenge_token, methods, expires_in }
   *   - { status: 'mfa_setup_required', setup_token, expires_in, message }
   *   - { status: 'password_change_required', change_token, expires_in, message }
   *   - { status: 'error', message }
   */
  async function login(email, password) {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await rpcClient.login(email, password);

      if (response?.status === 'mfa_challenge') {
        return {
          status: 'mfa_challenge',
          challenge_token: response.challenge_token,
          methods: response.methods || [],
          expires_in: response.expires_in,
        };
      }

      if (response?.status === 'mfa_setup_required') {
        return {
          status: 'mfa_setup_required',
          setup_token: response.setup_token,
          expires_in: response.expires_in,
          message: response.message,
        };
      }

      if (response?.status === 'password_change_required') {
        return forcedPasswordChangeResult(response);
      }

      applyAuthSession(response);
      return { status: 'authenticated' };
    } catch (err) {
      error.value = err.message || 'Login failed';
      return { status: 'error', message: err.message };
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Persist the user + token + permissions returned by the backend.
   * Shared by the password-only path, the MFA verify path, and forced
   * bootstrap so all three end up in the same hub_* localStorage keys.
   *
   * @param {Object} response
   */
  function applyAuthSession(response) {
    if (!response?.access_token) {
      return;
    }

    // A session is starting. Any `change_token` still parked from an abandoned
    // forced-change flow belongs to a different login attempt — possibly a
    // different account in this same tab — and must not outlive this one
    // (#1198).
    clearForcedPasswordChange();

    user.value = response.user;
    token.value = response.access_token;
    rpcClient.setAuthToken(response.access_token);

    if (response.permissions) {
      roles.value = Array.isArray(response.permissions.roles)
        ? response.permissions.roles
        : Object.values(response.permissions.roles ?? {});
      permissions.value = Array.isArray(response.permissions.actions)
        ? response.permissions.actions
        : Object.values(response.permissions.actions ?? {});
    }

    localStorage.setItem('hub_token', response.access_token);
    localStorage.setItem('hub_user', JSON.stringify(response.user));
    localStorage.setItem('hub_roles', JSON.stringify(roles.value));
    localStorage.setItem('hub_permissions', JSON.stringify(permissions.value));
  }

  /**
   * Normalise a `password_change_required` response (PCI DSS 8.3.5, #1198).
   * Raised only once every authentication factor has passed, so it always
   * arrives instead of an access token — never alongside one.
   *
   * @param {Object} response
   */
  function forcedPasswordChangeResult(response) {
    return {
      status: 'password_change_required',
      change_token: response.change_token,
      expires_in: response.expires_in,
      message: response.message,
    };
  }

  /**
   * Complete a forced password change (PCI DSS 8.3.5, #1198) and start the
   * session with the access token login withheld.
   *
   * @param {string} changeToken
   * @param {string} currentPassword
   * @param {string} newPassword
   * @returns {Promise<{ status: 'authenticated' } | { status: 'error', message: string }>}
   */
  async function completeForcedPasswordChange(changeToken, currentPassword, newPassword) {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await rpcClient.completeForcedPasswordChange(
        changeToken,
        currentPassword,
        newPassword
      );

      // Same reasoning as verifyMfa: this response carries no permissions block.
      roles.value = [];
      permissions.value = [];
      applyAuthSession(response);
      await fetchPermissions();
      return { status: 'authenticated' };
    } catch (err) {
      error.value = err.message || 'Could not change your password';
      return { status: 'error', message: err.message };
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Clear the forced-change gate from an ALREADY authenticated session — the
   * case where the token was minted before an administrator reset the password.
   * The RPC middleware keeps `users@changePassword` reachable for exactly this.
   *
   * @param {string} currentPassword
   * @param {string} newPassword
   * @returns {Promise<{ status: 'authenticated' } | { status: 'error', message: string }>}
   */
  async function changeOwnPassword(currentPassword, newPassword) {
    try {
      isLoading.value = true;
      error.value = null;

      await rpcClient.changeOwnPassword(currentPassword, newPassword);
      if (user.value) {
        user.value = { ...user.value, must_change_password: false };
        localStorage.setItem('hub_user', JSON.stringify(user.value));
      }
      return { status: 'authenticated' };
    } catch (err) {
      error.value = err.message || 'Could not change your password';
      return { status: 'error', message: err.message };
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Verify an MFA challenge (login-time) and complete the session on success.
   * `verifyMfa` returns the same shape as a straight login on success.
   * Permissions are not bundled with the verify response, so fetch them
   * separately to populate the gating data the rest of the hub relies on.
   *
   * @param {string} challengeToken
   * @param {string} type
   * @param {Object} payload
   * @param {boolean} [rememberDevice=false] When true, the backend issues a
   *   14-day trusted-device cookie so subsequent logins from this browser
   *   skip the MFA step. Hub-only — admin always re-challenges.
   * @returns {Promise<{ status: 'authenticated' } | { status: 'error', message: string }>}
   */
  async function verifyMfa(challengeToken, type, payload, rememberDevice = false) {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await rpcClient.verifyMfa(challengeToken, type, payload, rememberDevice);

      // Completing MFA is what issues the access token for an enrolled user, so
      // the forced-change gate (PCI DSS 8.3.5, #1198) can answer here instead.
      // Checked BEFORE applyAuthSession/fetchPermissions, which have no token to
      // work with.
      if (response?.status === 'password_change_required') {
        return forcedPasswordChangeResult(response);
      }

      // The `mfa@verify` response does not include a permissions block,
      // so explicitly zero the refs before persisting. Otherwise
      // applyAuthSession would persist whatever stale roles/permissions
      // happened to be in memory (e.g. from a previous user whose
      // logout() didn't run because their session expired via 401).
      // This is the BLOCK-level fix from PR #746's Codex / Gemini review.
      roles.value = [];
      permissions.value = [];
      applyAuthSession(response);
      await fetchPermissions();
      return { status: 'authenticated' };
    } catch (err) {
      error.value = err.message || 'Verification failed';
      return { status: 'error', message: err.message };
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Fetch the caller's permissions and roles via auth@me. Called after
   * the MFA verify path because verify doesn't return permissions inline.
   */
  async function fetchPermissions() {
    try {
      // The object-vs-array coercion that used to live here now lives in
      // BaseRpcClient.getAuthContext(), which owns the auth@me shape for
      // every app.
      const context = await rpcClient.getAuthContext();

      roles.value = context.roles;
      permissions.value = context.actions;
      applyPasswordChangeGate(context.must_change_password);
      localStorage.setItem('hub_roles', JSON.stringify(roles.value));
      localStorage.setItem('hub_permissions', JSON.stringify(permissions.value));
    } catch (err) {
      // Non-fatal: the user is authenticated, the permission list will
      // refresh on the next navigation.
      console.error('Failed to fetch permissions:', err);
    }
  }

  /**
   * Record the server's current forced-change verdict on the cached user
   * (PCI DSS 8.3.5, #1198).
   *
   * `auth@me` is the one method the RPC middleware still answers for a gated
   * session, which is exactly why this is read from there. Without it a
   * resumed session renders the dashboard off cached localStorage and only
   * discovers the obligation when some unrelated RPC happens to 403 — and a
   * screen that makes no RPC calls at all never discovers it.
   *
   * @param {boolean} mustChange
   */
  function applyPasswordChangeGate(mustChange) {
    if (!user.value) {
      return;
    }

    user.value = { ...user.value, must_change_password: mustChange === true };
    localStorage.setItem('hub_user', JSON.stringify(user.value));
  }

  async function logout() {
    try {
      isLoading.value = true;
      await rpcClient.logout();
    } catch (_err) {
      // Ignore logout errors
    } finally {
      user.value = null;
      token.value = null;
      roles.value = [];
      permissions.value = [];
      localStorage.removeItem('hub_token');
      localStorage.removeItem('hub_user');
      localStorage.removeItem('hub_roles');
      localStorage.removeItem('hub_permissions');
      isLoading.value = false;

      // Reset merchant store
      const { useMerchantStore } = await import('./merchant');
      const merchantStore = useMerchantStore();
      merchantStore.reset();
    }
  }

  async function initializeAuth() {
    const storedToken = localStorage.getItem('hub_token');
    const storedUser = localStorage.getItem('hub_user');
    const storedRoles = localStorage.getItem('hub_roles');
    const storedPermissions = localStorage.getItem('hub_permissions');

    if (storedToken && storedUser) {
      // The router guard and App.vue both call this on boot; re-hydrating a
      // session that is already live would just double the auth@me round trip.
      if (token.value === storedToken && user.value) {
        return;
      }

      token.value = storedToken;
      user.value = JSON.parse(storedUser);
      rpcClient.setAuthToken(storedToken);

      if (storedRoles) {
        const parsed = JSON.parse(storedRoles);
        roles.value = Array.isArray(parsed) ? parsed : Object.values(parsed ?? {});
      }

      if (storedPermissions) {
        const parsed = JSON.parse(storedPermissions);
        permissions.value = Array.isArray(parsed) ? parsed : Object.values(parsed ?? {});
      }

      // Ask the server what this session may actually do before rendering off
      // cached localStorage. It is the only way a resumed session learns that
      // its permissions changed or that it is now gated behind a forced
      // password change (#1198). Failures are swallowed by fetchPermissions,
      // leaving the cached values in place.
      await fetchPermissions();
    }
  }

  function clearError() {
    error.value = null;
  }

  return {
    // State
    user,
    token,
    roles,
    permissions,
    isLoading,
    error,

    // RPC Client
    rpcClient,

    // Getters
    isAuthenticated,
    userName,
    userEmail,
    mustChangePassword,
    hasPermission,

    // Actions
    login,
    verifyMfa,
    completeForcedPasswordChange,
    changeOwnPassword,
    applyAuthSession,
    fetchPermissions,
    logout,
    initializeAuth,
    clearError,
  };
});
